import { useQuery } from "@tanstack/react-query";
import { Ban, Check, CircleAlert, Clock, CreditCard, Gift } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../api/client";
import { type ChipTone, StatusChip } from "../components/kit";
import { Alert } from "../components/ui";

// Shared pieces of the super admin back-office: API helper, formatting (money, dates, USD), Romanian labels for
// the raw states the database keeps, and the small layout components every page uses.

export type Row = Record<string, unknown>;

export const get = <T,>(path: string) => api<T>("GET", `/admin${path}`);
export const post = <T,>(path: string, body?: unknown) => api<T>("POST", `/admin${path}`, body);

export function useAdmin<T>(key: unknown[], path: string) {
  return useQuery({ queryKey: ["admin", ...key], queryFn: () => get<T>(path) });
}

export const text = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : String(v));
export const when = (ms: unknown) =>
  typeof ms === "number" && ms > 0
    ? new Date(ms).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })
    : "—";
export const day = (ms: unknown) =>
  typeof ms === "number" && ms > 0 ? new Date(ms).toLocaleDateString("ro-RO", { dateStyle: "medium" }) : "—";
/** Whole days from now until `ms` (negative = in the past). */
export const daysUntil = (ms: unknown) =>
  typeof ms === "number" ? Math.ceil((ms - Date.now()) / 86_400_000) : null;

/** AI costs are kept in micro-USD. */
export const usd = (micro: unknown, digits = 2) =>
  `${(Number(micro ?? 0) / 1_000_000).toFixed(digits).replace(".", ",")} $`;

/** Minor units of a currency, e.g. 12947 RON → "129,47 lei". */
export function money(minor: unknown, currency: unknown) {
  const n = Number(minor ?? 0) / 100;
  const c = String(currency ?? "RON").toUpperCase();
  const s = n.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return c === "RON" ? `${s} lei` : c === "EUR" ? `${s} €` : `${s} ${c}`;
}

/** { RON: 12947, EUR: 1990 } → "129,47 lei · 19,90 €" (or "0" when empty). */
export function moneyMap(m: unknown) {
  const entries = Object.entries((m ?? {}) as Record<string, number>).filter(([, v]) => v);
  return entries.length ? entries.map(([c, v]) => money(v, c)).join(" · ") : "0";
}

export const SUB_LABEL: Record<string, { label: string; tone: ChipTone }> = {
  trialing: { label: "Probă gratuită", tone: "info" },
  active: { label: "Plătește", tone: "success" },
  past_due: { label: "Plată restantă", tone: "warning" },
  canceled: { label: "Anulat", tone: "neutral" },
  expired: { label: "Expirat", tone: "error" },
  beta: { label: "Acces gratuit", tone: "neutral" },
};

export function SubChip({ status, trialEndsAt }: { status: unknown; trialEndsAt?: unknown }) {
  const s = SUB_LABEL[String(status)] ?? { label: text(status), tone: "neutral" as ChipTone };
  const left = status === "trialing" ? daysUntil(trialEndsAt) : null;
  const label =
    left === null ? s.label : left < 0 ? "Probă încheiată, neplătit" : `${s.label} · ${left} zile`;
  const tone: ChipTone =
    left !== null && left < 0 ? "error" : left !== null && left <= 3 ? "warning" : s.tone;
  const icon =
    status === "active" ? CreditCard : status === "trialing" ? Clock : status === "beta" ? Gift : CircleAlert;
  return <StatusChip tone={tone} icon={icon} label={label} />;
}

export function RestaurantChip({ status }: { status: unknown }) {
  return status === "active" ? (
    <StatusChip tone="success" icon={Check} label="Activ" />
  ) : status === "suspended" ? (
    <StatusChip tone="error" icon={Ban} label="Suspendat" />
  ) : (
    <StatusChip tone="neutral" icon={Ban} label="Închis" />
  );
}

export const IMPORT_LABEL: Record<string, string> = {
  uploading: "Se încarcă",
  queued: "În coadă",
  processing: "Se procesează",
  ready: "Gata",
  blocked: "Blocat",
  failed: "Eșuat",
  cancelled: "Anulat",
};

export const FISCAL_LABEL: Record<string, { label: string; tone: ChipTone }> = {
  none: { label: "Fără factură fiscală", tone: "neutral" },
  pending: { label: "În așteptare", tone: "info" },
  issuing: { label: "Se emite", tone: "info" },
  issued: { label: "Emisă", tone: "success" },
  failed: { label: "Refuzată", tone: "error" },
};

export const fiscalOf = (s: unknown) =>
  FISCAL_LABEL[String(s)] ?? { label: "Fără factură fiscală", tone: "neutral" as ChipTone };

export const PRODUCT_LABEL: Record<string, string> = {
  addon: "Valori nutriționale",
  renewal: "Valori nutriționale (reînnoire)",
  pack5: "Pachet 5 analize",
  pack10: "Pachet 10 analize",
};

export const ROLE_LABEL: Record<string, string> = {
  owner: "Proprietar",
  manager: "Manager",
  staff: "Personal",
};

/** Query states: loading line, error alert, then the content. */
export function Loadable<T>({
  q,
  children,
}: {
  q: { isPending: boolean; isError: boolean; error: unknown; data?: T };
  children: (d: T) => ReactNode;
}) {
  if (q.isPending)
    return (
      <p className="muted" role="status">
        Se încarcă…
      </p>
    );
  if (q.isError || q.data === undefined)
    return (
      <Alert tone="error" live>
        {q.error instanceof Error ? q.error.message : "Eroare la încărcare."}
      </Alert>
    );
  return <>{children(q.data)}</>;
}

export function PageHead({ title, sub, actions }: { title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="admin-head">
      <div className="stack" style={{ gap: 4, minWidth: 0 }}>
        <h1 className="h1">{title}</h1>
        {sub && (
          <p className="muted" style={{ margin: 0 }}>
            {sub}
          </p>
        )}
      </div>
      {actions && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {actions}
        </div>
      )}
    </div>
  );
}

/** A key figure; `to` makes the whole card a link, `tone` highlights what needs attention. */
export function Kpi({
  label,
  value,
  hint,
  to,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  to?: string;
  tone?: "warning" | "error";
}) {
  const body = (
    <>
      <span className="k">{label}</span>
      <span className="v">{value}</span>
      {hint && <span className="h">{hint}</span>}
    </>
  );
  const cls = `kpi${tone ? ` kpi-${tone}` : ""}`;
  return to ? (
    <Link to={to} className={`${cls} kpi-link`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function Section({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <h2 className="h2" style={{ margin: 0 }}>
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Table({
  head,
  rows,
  empty = "Nimic de afișat.",
}: {
  head: string[];
  rows: ReactNode[][];
  empty?: string;
}) {
  if (!rows.length)
    return (
      <p className="muted" style={{ margin: 0 }}>
        {empty}
      </p>
    );
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static rows of one render
            <tr key={i}>
              {r.map((cell, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static cells of one row
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SearchBox({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <label className="stack" style={{ gap: 4, flex: "1 1 260px", maxWidth: 420 }}>
      <span className="field-label">{label}</span>
      <input className="field-input" type="search" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function Filter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <label className="stack" style={{ gap: 4, flex: "0 1 220px" }}>
      <span className="field-label">{label}</span>
      <select className="field-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

/** "Name" linking to the restaurant's 360° page. */
export function RestaurantLink({ id, name }: { id: unknown; name: unknown }) {
  return <Link to={`/admin/restaurante/${String(id)}`}>{text(name)}</Link>;
}
